/**
 * Document titles and meta descriptions for /dark-sky-locations/:slug.
 *
 * Ahrefs flags titles over about 60 characters and descriptions outside
 * about 110–160. The old template put "Dark Sky Photography at " and
 * "| Milky Way Planner" around the official name, so almost every page
 * was over 60, and the description grew with the name past 160.
 *
 * Titles keep the official name first and never cut it mid-word. A long
 * name loses its trailing designation, then splits at a natural break
 * (" and ", " & ", …) with a short brand suffix, then trailing words.
 * Descriptions are built only from fields on the location row.
 */
import { typeLabel } from './locationHubs';

const GUIDE = ' Milky Way Guide | MWP';
const BRAND = ' | MWP';
const MIN_DESCRIPTION = 120;
const MAX_DESCRIPTION = 155;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Longest first, so "International Dark Sky Park" wins over "Dark Sky Park". */
const DESIGNATIONS = [
  'Starlight Reserve and Starlight Tourism Destination',
  'Starlight Reserve and Tourism Destination',
  'Conservation Area for Dark and Starry Sky',
  'International Dark Sky Sanctuary',
  'International Dark Sky Community',
  'International Night Sky Province',
  'International Dark Sky Reserve',
  'International Dark Sky Park',
  'Starlight Tourism Destination',
  'Urban Night Sky Place',
  'Starlight Walking Trail',
  'Starlight Stellar Park',
  'Starlight Wilderness',
  'Dark Sky Sanctuary',
  'Dark Sky Community',
  'Nocturnal Preserve',
  'Dark Sky Preserve',
  'Starlight Reserve',
  'Dark Sky Reserve',
  'Dark Sky Island',
  'Dark Sky Park',
  'Starlight Camp',
  'Stellar Park',
].sort((a, b) => b.length - a.length);

const DANGLING = new Set([
  'and', 'or', 'of', 'the', 'a', 'an', 'y', 'de', 'del', 'da', 'di', 'la', 'le', 'el',
  'for', 'at', 'in', 'on', 'to', 'with', '&', 'state', 'national',
]);

/** Longest first so " and " wins over a bare comma. */
const NATURAL_BREAKS = [' and ', ' & ', ' / ', ' (', ' - ', ','] as const;

export interface LocationSeoFields {
  name: string;
  country: string;
  state_province: string | null;
  type: string;
  bortle_class: number | null;
  best_months: number[] | null;
  peak_core_altitude: number | null;
  darkness_hours_peak: number | null;
  recognition_year: number | null;
  recognizing_body: string | null;
  dsag_class: string | null;
  area_hectares: number | null;
}

function tidy(value: string): string {
  const words = value.replace(/[\s,;:–—\-/]+$/u, '').trim().split(/\s+/).filter(Boolean);
  while (words.length > 1 && DANGLING.has(words[words.length - 1].toLowerCase().replace(/[.,]+$/g, ''))) {
    words.pop();
  }
  return words.join(' ');
}

function withBrand(base: string): string | null {
  if (`${base}${GUIDE}`.length <= 60) return `${base}${GUIDE}`;
  if (`${base}${BRAND}`.length <= 60) return `${base}${BRAND}`;
  if (base.length <= 60 && base.length > 0) return base;
  return null;
}

function stripDesignation(name: string): string | null {
  const lower = name.toLowerCase();
  for (const phrase of DESIGNATIONS) {
    if (!lower.endsWith(phrase.toLowerCase())) continue;
    const cut = tidy(name.slice(0, name.length - phrase.length));
    if (cut && cut.length < name.length) return cut;
  }
  return null;
}

function withBrandShort(base: string): string | null {
  if (`${base}${BRAND}`.length <= 60) return `${base}${BRAND}`;
  if (base.length <= 60 && base.length > 0) return base;
  return null;
}

function cutAtNaturalBreak(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  for (const sep of NATURAL_BREAKS) {
    const idx = trimmed.indexOf(sep);
    if (idx <= 0) continue;
    const titled = withBrandShort(tidy(trimmed.slice(0, idx)));
    if (titled) return titled;
  }
  return null;
}

function dropWords(base: string): string | null {
  const words = base.split(/\s+/);
  for (let end = words.length - 1; end >= 1; end--) {
    const titled = withBrand(tidy(words.slice(0, end).join(' ')));
    if (titled) return titled;
  }
  return null;
}

/** The place-name portion of a title, without the brand suffix. */
export function locationTitleName(title: string): string {
  if (title.endsWith(GUIDE)) return title.slice(0, -GUIDE.length);
  if (title.endsWith(BRAND)) return title.slice(0, -BRAND.length);
  return title;
}

export function locationTitle(name: string): string {
  const stripped = stripDesignation(name);
  return (
    withBrand(name) ??
    withBrand(stripped ?? '') ??
    cutAtNaturalBreak(name) ??
    (stripped ? cutAtNaturalBreak(stripped) : null) ??
    dropWords(stripped ?? name) ??
    tidy(name)
  );
}

function article(label: string): string {
  return /^[aeiou]/i.test(label) ? 'an' : 'a';
}

function where(loc: LocationSeoFields): string {
  return [loc.state_province, loc.country].filter(Boolean).join(', ');
}

function monthList(months: number[], full: boolean): string {
  const names = full ? MONTH_FULL : MONTH_SHORT;
  return months.map((month) => names[month - 1]).filter(Boolean).join(', ');
}

function hours(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text} hour${text === '1' ? '' : 's'} of peak darkness.`;
}

function appendWhile(text: string, parts: Array<string | null>, max: number): string {
  for (const part of parts) {
    if (!part) continue;
    const next = `${text} ${part}`;
    if (next.length <= max) text = next;
  }
  return text;
}

export function locationDescription(loc: LocationSeoFields): string {
  const label = typeLabel(loc.type);
  const place = where(loc);
  let core = `${loc.name} is ${article(label)} ${label} in ${place}.`;
  if (core.length > MAX_DESCRIPTION) core = `${loc.name}, ${place}.`;
  if (core.length > MAX_DESCRIPTION) core = `${loc.name}, ${loc.country}.`;

  const shortMonths = loc.best_months?.length ? `Best months: ${monthList(loc.best_months, false)}.` : null;
  const fullMonths = loc.best_months?.length ? `Best months: ${monthList(loc.best_months, true)}.` : null;
  const optional: Array<string | null> = [
    loc.bortle_class != null ? `Bortle class ${loc.bortle_class}.` : null,
    shortMonths,
    loc.peak_core_altitude != null ? `Peak core altitude ${Math.round(loc.peak_core_altitude)}°.` : null,
    loc.darkness_hours_peak != null ? hours(loc.darkness_hours_peak) : null,
    loc.recognition_year != null ? `Recognized in ${loc.recognition_year}.` : null,
    'Free Milky Way visibility calendar.',
  ];

  let text = appendWhile(core, optional, MAX_DESCRIPTION);
  if (text.length < MIN_DESCRIPTION && fullMonths && shortMonths && text.includes(shortMonths)) {
    const swapped = text.replace(shortMonths, fullMonths);
    if (swapped.length <= MAX_DESCRIPTION) text = swapped;
  }
  if (text.length < MIN_DESCRIPTION) {
    text = appendWhile(
      text,
      [
        loc.area_hectares != null ? `Covers ${loc.area_hectares.toLocaleString('en-US')} hectares.` : null,
        loc.recognizing_body ? `Recognized by ${loc.recognizing_body}.` : null,
        loc.dsag_class ? `DSAG class ${loc.dsag_class}.` : null,
        'Open the free visibility calendar.',
      ],
      MAX_DESCRIPTION,
    );
  }
  return text;
}
